'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'bs';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.solutions': 'Solutions',
    'nav.portfolio': 'Portfolio',
    'nav.custom_plan': 'Get Custom Plan',
    'nav.about': 'About',
    'nav.technology': 'Technology',
    'nav.support': 'Support',
    'nav.more': 'More',
    
    // Common
    'common.learn_more': 'Learn More',
    'common.get_started': 'Get Started',
    'common.contact_us': 'Contact Us',
    'common.read_more': 'Read More',
    'common.view_all': 'View All',
    'common.submit': 'Submit',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.close': 'Close',
    
    // Homepage Hero
    'home.hero.trusted': 'Trusted by 200+ Enterprise Clients',
    'home.hero.slide1.title': 'Transform Your Business with Precision Engineering',
    'home.hero.slide1.subtitle': 'We architect digital ecosystems that propel mid-to-large enterprises into their next growth phase',
    'home.hero.slide1.cta': 'Get Your Custom Plan',
    'home.hero.slide2.title': 'Strategic Technology Partnership',
    'home.hero.slide2.subtitle': "Beyond development—we're your trusted guide through digital complexity",
    'home.hero.slide2.cta': 'Explore Solutions',
    'home.hero.slide3.title': 'Measurable Business Impact',
    'home.hero.slide3.subtitle': 'ROI-focused software solutions backed by meticulous planning and cutting-edge execution',
    'home.hero.slide3.cta': 'View Case Studies',
    'home.hero.watch_stories': 'Watch Success Stories',
    
    // About Hero Section
    'about.hero.title': 'Where Precision Meets Innovation',
    'about.hero.description': "We don't just build software—we architect digital ecosystems that propel businesses into their next growth phase",
    'about.hero.orbital_precision': 'Orbital Precision',
    'about.hero.kinetic_innovation': 'Kinetic Innovation',
    'about.hero.confident_transformation': 'Confident Transformation',
    
    // Custom Software Solutions
    'solutions.hero.title': 'Custom Software Solutions',
    'solutions.hero.subtitle': 'Enterprise-Grade Development Tailored to Your Business',
    'solutions.hero.description': 'Transform complex challenges into competitive advantages with our precision-engineered software solutions',
    'solutions.services.title': 'Our Services',
    'solutions.services.web': 'Web Applications',
    'solutions.services.mobile': 'Mobile Development',
    'solutions.services.cloud': 'Cloud Solutions',
    'solutions.services.enterprise': 'Enterprise Software',
    
    // Portfolio & Case Studies
    'portfolio.hero.title': 'Portfolio & Case Studies',
    'portfolio.hero.subtitle': 'Real Results, Real Impact',
    'portfolio.filter.all': 'All Projects',
    'portfolio.filter.web': 'Web Apps',
    'portfolio.filter.mobile': 'Mobile Apps',
    'portfolio.filter.enterprise': 'Enterprise',
    'portfolio.roi.title': 'ROI Calculator',
    'portfolio.roi.subtitle': 'Calculate Your Potential Return',
    
    // Technology Expertise
    'tech.hero.title': 'Technology Expertise',
    'tech.hero.subtitle': 'Cutting-Edge Technologies, Battle-Tested Solutions',
    'tech.stack.frontend': 'Frontend Technologies',
    'tech.stack.backend': 'Backend Technologies',
    'tech.stack.cloud': 'Cloud Platforms',
    'tech.stack.database': 'Databases',
    
    // Support & Maintenance
    'support.hero.title': 'Support & Maintenance',
    'support.hero.subtitle': '24/7 Technical Support for Your Critical Systems',
    'support.plans.title': 'Support Plans',
    'support.plans.basic': 'Basic Support',
    'support.plans.premium': 'Premium Support',
    'support.plans.enterprise': 'Enterprise Support',
    'support.emergency.title': 'Emergency Support',
    'support.emergency.available': 'Available 24/7/365',
    
    // Get Custom Plan
    'custom_plan.title': 'Get Your Custom Plan',
    'custom_plan.subtitle': 'Tell us about your project and we\'ll create a tailored solution',
    'custom_plan.badge': '24-Hour Response Commitment',
    'custom_plan.hero_title': 'Get Your Custom Development Plan',
    'custom_plan.hero_subtitle': 'Tell us about your project and receive a comprehensive development plan with detailed scope, timeline estimates, and budget projections. Our team will contact you within 24 hours to discuss your vision.',
    'custom_plan.fast_response_title': 'Fast Response',
    'custom_plan.fast_response_desc': 'Get a detailed response from our team within 24 hours of submission',
    'custom_plan.instant_estimates_title': 'Instant Estimates',
    'custom_plan.instant_estimates_desc': 'Receive preliminary budget and timeline estimates based on your requirements',
    'custom_plan.secure_title': 'Secure & Confidential',
    'custom_plan.secure_desc': 'Your information is encrypted and protected with enterprise-grade security',
    'custom_plan.step1': 'Company Information',
    'custom_plan.step2': 'Project Details',
    'custom_plan.step3': 'Requirements',
    'custom_plan.step4': 'Document Upload',
    'custom_plan.step5': 'Review & Submit',
    'custom_plan.company_name': 'Company Name',
    'custom_plan.email': 'Email Address',
    'custom_plan.phone': 'Phone Number',
    'custom_plan.project_title': 'Project Title',
    'custom_plan.project_description': 'Project Description',
    'custom_plan.budget': 'Estimated Budget',
    'custom_plan.timeline': 'Desired Timeline',
    
    // Footer
    'footer.company': 'Company',
    'footer.services': 'Services',
    'footer.resources': 'Resources',
    'footer.contact': 'Contact',
    'footer.email': 'Email',
    'footer.phone': 'Phone',
    'footer.address': 'Address',
    'footer.copyright': '© 2025 Orbinetic Systems. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
  },
  bs: {
    // Navigation
    'nav.solutions': 'Rješenja',
    'nav.portfolio': 'Portfolio',
    'nav.custom_plan': 'Prilagođeni Plan',
    'nav.about': 'O Nama',
    'nav.technology': 'Tehnologija',
    'nav.support': 'Podrška',
    'nav.more': 'Više',
    
    // Common
    'common.learn_more': 'Saznaj Više',
    'common.get_started': 'Započni',
    'common.contact_us': 'Kontaktiraj Nas',
    'common.read_more': 'Pročitaj Više',
    'common.view_all': 'Prikaži Sve',
    'common.submit': 'Pošalji',
    'common.next': 'Sljedeće',
    'common.previous': 'Prethodno',
    'common.close': 'Zatvori',
    
    // Homepage Hero
    'home.hero.trusted': 'Pouzdani od strane 200+ Poslovnih Klijenata',
    'home.hero.slide1.title': 'Transformišite Vaš Biznis Preciznim Inženjeringom',
    'home.hero.slide1.subtitle': 'Arhitekturiramo digitalne ekosisteme koji pokreću srednja i velika preduzeća u njihovu sljedeću fazu rasta',
    'home.hero.slide1.cta': 'Dobij Prilagođeni Plan',
    'home.hero.slide2.title': 'Strateško Tehnološko Partnerstvo',
    'home.hero.slide2.subtitle': 'Više od razvoja—mi smo vaš pouzdani vodič kroz digitalnu kompleksnost',
    'home.hero.slide2.cta': 'Istraži Rješenja',
    'home.hero.slide3.title': 'Mjerljiv Poslovni Uticaj',
    'home.hero.slide3.subtitle': 'ROI-fokusirana softverska rješenja podržana pažljivim planiranjem i najsavremenijom realizacijom',
    'home.hero.slide3.cta': 'Pogledaj Studije Slučaja',
    'home.hero.watch_stories': 'Pogledaj Priče o Uspjehu',
    
    // About Hero Section
    'about.hero.title': 'Gdje se Preciznost Susreće sa Inovacijom',
    'about.hero.description': 'Ne samo da pravimo softver—arhitekturiramo digitalne ekosisteme koji pokreću preduzeća u njihovu sljedeću fazu rasta',
    'about.hero.orbital_precision': 'Orbitalna Preciznost',
    'about.hero.kinetic_innovation': 'Kinetička Inovacija',
    'about.hero.confident_transformation': 'Sigurna Transformacija',
    
    // Custom Software Solutions
    'solutions.hero.title': 'Prilagođena Softverska Rješenja',
    'solutions.hero.subtitle': 'Razvoj na Nivou Preduzeća Prilagođen Vašem Poslovanju',
    'solutions.hero.description': 'Transformišite složene izazove u konkurentske prednosti sa našim precizno inženjeriranim softverskim rješenjima',
    'solutions.services.title': 'Naše Usluge',
    'solutions.services.web': 'Web Aplikacije',
    'solutions.services.mobile': 'Mobilni Razvoj',
    'solutions.services.cloud': 'Cloud Rješenja',
    'solutions.services.enterprise': 'Poslovni Softver',
    
    // Portfolio & Case Studies
    'portfolio.hero.title': 'Portfolio i Studije Slučaja',
    'portfolio.hero.subtitle': 'Stvarni Rezultati, Stvaran Uticaj',
    'portfolio.filter.all': 'Svi Projekti',
    'portfolio.filter.web': 'Web Aplikacije',
    'portfolio.filter.mobile': 'Mobilne Aplikacije',
    'portfolio.filter.enterprise': 'Poslovno',
    'portfolio.roi.title': 'ROI Kalkulator',
    'portfolio.roi.subtitle': 'Izračunaj Svoj Potencijalni Povrat',
    
    // Technology Expertise
    'tech.hero.title': 'Tehnološka Ekspertiza',
    'tech.hero.subtitle': 'Najsavremenije Tehnologije, Provjerena Rješenja',
    'tech.stack.frontend': 'Frontend Tehnologije',
    'tech.stack.backend': 'Backend Tehnologije',
    'tech.stack.cloud': 'Cloud Platforme',
    'tech.stack.database': 'Baze Podataka',
    
    // Support & Maintenance
    'support.hero.title': 'Podrška i Održavanje',
    'support.hero.subtitle': '24/7 Tehnička Podrška za Vaše Kritične Sisteme',
    'support.plans.title': 'Planovi Podrške',
    'support.plans.basic': 'Osnovna Podrška',
    'support.plans.premium': 'Premium Podrška',
    'support.plans.enterprise': 'Poslovna Podrška',
    'support.emergency.title': 'Hitna Podrška',
    'support.emergency.available': 'Dostupno 24/7/365',
    
    // Get Custom Plan
    'custom_plan.title': 'Dobij Svoj Prilagođeni Plan',
    'custom_plan.subtitle': 'Recite nam o vašem projektu i stvorićemo rješenje po mjeri',
    'custom_plan.badge': '24-satna obaveza odgovora',
    'custom_plan.hero_title': 'Dobij svoj prilagođeni plan razvoja',
    'custom_plan.hero_subtitle': 'Recite nam o projektu i dobit ćete sveobuhvatan plan sa detaljnim obimom, procjenom rokova i budžeta. Naš tim će vas kontaktirati u roku od 24 sata kako bismo razgovarali o vašoj viziji.',
    'custom_plan.fast_response_title': 'Brz Odgovor',
    'custom_plan.fast_response_desc': 'Detaljan odgovor našeg tima u roku od 24 sata nakon prijave',
    'custom_plan.instant_estimates_title': 'Trenutne Procjene',
    'custom_plan.instant_estimates_desc': 'Dobijte preliminarne procjene budžeta i rokova na osnovu vaših zahtjeva',
    'custom_plan.secure_title': 'Sigurno i Povjerljivo',
    'custom_plan.secure_desc': 'Vaši podaci su šifrovani i zaštićeni sigurnosnim standardima na nivou preduzeća',
    'custom_plan.step1': 'Informacije o Kompaniji',
    'custom_plan.step2': 'Detalji Projekta',
    'custom_plan.step3': 'Zahtjevi',
    'custom_plan.step4': 'Upload Dokumenata',
    'custom_plan.step5': 'Pregled i Slanje',
    'custom_plan.company_name': 'Naziv Kompanije',
    'custom_plan.email': 'Email Adresa',
    'custom_plan.phone': 'Broj Telefona',
    'custom_plan.project_title': 'Naziv Projekta',
    'custom_plan.project_description': 'Opis Projekta',
    'custom_plan.budget': 'Procijenjeni Budžet',
    'custom_plan.timeline': 'Željeni Vremenski Okvir',
    
    // Footer
    'footer.company': 'Kompanija',
    'footer.services': 'Usluge',
    'footer.resources': 'Resursi',
    'footer.contact': 'Kontakt',
    'footer.email': 'Email',
    'footer.phone': 'Telefon',
    'footer.address': 'Adresa',
    'footer.copyright': '© 2025 Orbinetic Systems. Sva prava zadržana.',
    'footer.privacy': 'Politika Privatnosti',
    'footer.terms': 'Uslovi Korištenja',
  },
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'bs')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language]?.[key] || key;
  };

  return (
    <LanguageContext.Provider data-component-id="src/contexts/LanguageContext.tsx:270:4" data-component-path="src/contexts/LanguageContext.tsx" data-component-line="270" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
